import { useState } from 'react';
import { SelectOption } from '@anytag/shared-types';
import { Select, ErrorMessage, getValuesArray } from '@anytag/shared-atoms';
import { RHFFieldWrapper } from '../shared';
import { useFormatRHFFieldProps } from '../hooks';
import { MultipleSelectorFieldProps, SelectorFieldProps, SelectValueType, SingleSelectorFieldProps } from './types';

// Function overloads for proper type inference based on `multiple` prop
export function SelectorField<T extends string, TOption extends SelectOption<string> = SelectOption<string>>(
  props: MultipleSelectorFieldProps<T, TOption>
): React.ReactElement;
export function SelectorField<T extends string, TOption extends SelectOption<string> = SelectOption<string>>(
  props: SingleSelectorFieldProps<T, TOption>
): React.ReactElement;
export function SelectorField<T extends string, TOption extends SelectOption<string> = SelectOption<string>>(
  props: SelectorFieldProps<T, TOption>
): React.ReactElement {
  const [createdOptions, setCreatedOptions] = useState<TOption[]>([]);

  const {
    setValueOptions,
    fieldWrapperProps,
    formContext: { setValue },
    fieldRegister: { ref, ...restRegister },
    fieldProps: { name, onSubmit, onChange, multiple, options = [], ...restFieldProps }
  } = useFormatRHFFieldProps<SelectValueType<TOption>, T, SelectorFieldProps<T, TOption>>(props);

  const selectedValues = getValuesArray({ value: restFieldProps.value, multiple }) as string[]; // use string[] internally for comparisons
  // Filter createdOptions to only keep selected ones (remove unused temp options)
  const selectedCreatedOptions = createdOptions.filter((opt) => selectedValues.includes(opt.value));
  // Deduplicate options, put selected created options first
  const optionsMap = new Map([...options, ...selectedCreatedOptions].map((opt) => [opt.value, opt]));
  const mergedOptions = Array.from(optionsMap.values());

  return (
    <RHFFieldWrapper {...fieldWrapperProps} hideError>
      {({ hasError, errorMessage }) => (
        <>
          {multiple ? (
            <Select<TOption>
              {...restRegister}
              {...(restFieldProps as Partial<MultipleSelectorFieldProps<T, TOption>>)}
              multiple={true}
              options={options}
              invalid={hasError}
              onChange={(value) => {
                if (onChange) {
                  (onChange as MultipleSelectorFieldProps<T, TOption>['onChange'])?.(name, value, options);
                } else {
                  setValue<string>(name, value, setValueOptions);
                }
                onSubmit?.();
              }}
            />
          ) : (
            <Select<TOption>
              {...restRegister}
              {...(restFieldProps as Partial<SingleSelectorFieldProps<T, TOption>>)}
              multiple={false}
              invalid={hasError}
              options={mergedOptions}
              onChange={(value, action) => {
                const newOptions: TOption[] = [...mergedOptions];
                if (action === 'create') {
                  // Only add if not already exists (by value)
                  if (mergedOptions.findIndex((opt) => opt.value === value) === -1) {
                    const newOption = { label: value, value, _isNew: true } satisfies SelectOption<
                      string,
                      string
                    > as unknown as TOption;
                    newOptions.push(newOption);
                    setCreatedOptions((prev) => [...prev, newOption]);
                  }
                }

                if (onChange) {
                  (onChange as SingleSelectorFieldProps<T, TOption>['onChange'])?.(name, value, newOptions);
                } else {
                  setValue<string>(name, value, setValueOptions);
                }
                onSubmit?.();
              }}
            />
          )}
          <ErrorMessage ref={ref} message={errorMessage} />
        </>
      )}
    </RHFFieldWrapper>
  );
}
